import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../components/ui/Icon';
import { fmtEur, fmtDate } from '../../lib/utils';
import {
  METHODS, METHOD_LABEL, isoDay, suggestionsQuery, transactionsQuery, accountsQuery,
  type BankTransaction, type MatchKind, type PayMethod,
} from './adminApi';

/**
 * Popover "Segna incassata" / "Segna pagata": pochi campi, solo l'essenziale.
 * Si chiamava "Incassa" / "Paga", ma non paga niente: registra (Lorenzo, 24/9:
 * "Paga" fa credere che parta un bonifico). Il titolo e la riga in fondo lo
 * dicono: nessun soldo si muove, si segna soltanto.
 *   data (default oggi) · come è stata pagata · per bonifico, carta, addebito e
 *   ricevuta bancaria un select dei movimenti in banca compatibili (suggestions
 *   dell'API quando la voce esiste: rata fattura, rata fornitore, rata tassa,
 *   voce di cassa; per le spese fisse, che non hanno ancora una voce, i
 *   movimenti in uscita liberi che citano il fornitore).
 * Scegliere un movimento porta la data a quella del movimento: la verità è
 * l'estratto conto, non il giorno in cui lo si registra.
 *
 * È una registrazione DENTRO Masamune OS: su FattureInCloud non si scrive mai
 * (decisione del 19/09/2026). Sulle fatture (emesse e ricevute) lo si dice in
 * una riga, perché "incassa" accanto a una fattura fa pensare che cambi anche
 * lì; la chiusura finisce in Banca › Da comunicare al commercialista.
 */
export interface SettleValue {
  paidDate: string;
  method: PayMethod;
  bankTransactionId: string | null;
  accountId: string | null;
  /** Solo per fatture emesse/ricevute: perché la si segna qui e non su FattureInCloud. */
  localPaidNote?: string | null;
}

export function SettlePopover({ mode, amount, target, counterparty, side, pending, withNote, subtitle, adminData = true, avviso, onConfirm, onClose }: {
  mode: 'incassa' | 'paga';
  /**
   * Una riga in grassetto su quello che parte insieme alla registrazione (26/10/2026):
   * chiudendo l'ultima rata di una fattura parte la mail «Fattura incassata».
   */
  avviso?: ReactNode;
  amount: number;
  /** Voce da saldare: abilita le suggestions dell'API. */
  target?: { kind: MatchKind; id: string };
  /** Senza target: filtra i movimenti non abbinati per controparte. */
  counterparty?: string;
  side?: 'DEBIT' | 'CREDIT';
  pending?: boolean;
  /**
   * Mostra il campo nota e la riga su FattureInCloud. Si accende solo sulle
   * rate di fatture emesse e ricevute: quelle vivono anche su FattureInCloud e
   * segnarle qui crea una differenza da spiegare al commercialista. Tasse,
   * spese fisse e contanti su FattureInCloud non ci sono.
   */
  withNote?: boolean;
  /** Una riga sotto l'importo, es. "rata 1 di 3". */
  subtitle?: string | null;
  /**
   * Movimenti e conti arrivano dall'Amministrazione: chi incassa una fattura
   * con la sola Fatturazione (lista dei documenti) non li può leggere, e il
   * popover resta con data, metodo e nota.
   */
  adminData?: boolean;
  onConfirm: (v: SettleValue) => void;
  onClose: () => void;
}) {
  const mobile = useIsMobile();
  const [paidDate, setPaidDate] = useState(isoDay());
  const [method, setMethod] = useState<PayMethod>('BONIFICO');
  const [txId, setTxId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [note, setNote] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: accounts } = useQuery({ ...accountsQuery, enabled: adminData });
  const wantsBank = adminData && (method === 'BONIFICO' || method === 'CARTA' || method === 'RIBA' || method === 'ADDEBITO');

  // Suggerimenti: voce esistente → endpoint dedicato; altrimenti movimenti non abbinati per controparte
  const { data: suggested } = useQuery({ ...suggestionsQuery(target ?? { kind: 'INVOICE_PAYMENT', id: '' }), enabled: !!target && wantsBank });
  const { data: txPage } = useQuery({
    ...transactionsQuery({ side: side ?? 'DEBIT', unmatched: true, search: counterparty?.trim() || undefined, pageSize: 8 }),
    enabled: !target && wantsBank,
  });
  const candidates: BankTransaction[] = target ? (suggested ?? []) : (txPage?.items ?? []);

  // Conto di default: cassa se contanti, altrimenti il primo conto Qonto
  useEffect(() => {
    if (!accounts?.length) return;
    const want = method === 'CONTANTI' ? accounts.find((a) => a.provider === 'CASH') : accounts.find((a) => a.provider === 'QONTO');
    setAccountId(want?.id ?? '');
  }, [accounts, method]);

  const pickTx = (id: string) => {
    setTxId(id);
    const tx = candidates.find((t) => t.id === id);
    if (tx?.settledAt) setPaidDate(tx.settledAt.slice(0, 10));
  };

  const submit = () => {
    if (pending || !paidDate) return;
    onConfirm({ paidDate, method, bankTransactionId: txId || null, accountId: accountId || null, localPaidNote: note.trim() || null });
  };

  const incasso = mode === 'incassa';
  const titolo = incasso ? 'Segna incassata' : 'Segna pagata';

  // Sul telefono mobile.css ne fa un foglio dal basso (position: fixed). Dentro
  // la scheda di una tabella resterebbe sotto la barra in basso, bottone di
  // conferma compreso (la riga fa da contesto di impilamento): lo si porta in
  // fondo al documento, come "Segna pagato altrove" in Dipendenti.
  const corpo = (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div ref={ref} className="dropdown adm-pop right-0 mt-1 z-[61]" role="dialog" aria-label={titolo}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit(); }}>
        <div className="adm-pop-hd">
          <span className="t-micro">{titolo}</span>
          <span className="text-right">
            <span className="mono font-semibold">€ {fmtEur(amount, true)}</span>
            {subtitle && <span className="block text-[12px] mute">{subtitle}</span>}
          </span>
        </div>
        {/* 96px e non 76 (index.css): "PAGATA CON" andava a capo (26/10/2026). */}
        <div className="adm-pop-grid" style={{ gridTemplateColumns: '96px minmax(0, 1fr)' }}>
          <label className="field-label">{incasso ? 'Incassata il' : 'Pagata il'}</label>
          <input className="inp inp-sm mono" type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} autoFocus />
          <label className="field-label">Pagata con</label>
          <select className="inp inp-sm" value={method} onChange={(e) => { setMethod(e.target.value as PayMethod); setTxId(''); }}>
            {METHODS.map((m) => <option key={m} value={m}>{METHOD_LABEL[m]}</option>)}
          </select>
          {wantsBank && (
            <>
              <label className="field-label">Movimento</label>
              <select className="inp inp-sm" value={txId} onChange={(e) => pickTx(e.target.value)}>
                <option value="">{candidates.length ? 'Nessuno, senza movimento' : 'Nessun movimento trovato'}</option>
                {candidates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {fmtDate(t.settledAt)} · € {fmtEur(t.amount, true)} · {(t.counterparty || t.label).slice(0, 40)}
                  </option>
                ))}
              </select>
            </>
          )}
          {withNote && (
            <>
              <label className="field-label">Nota</label>
              <input
                className="inp inp-sm"
                type="text"
                maxLength={500}
                placeholder="Facoltativa, per il commercialista"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </>
          )}
          {!wantsBank && adminData && (accounts?.length ?? 0) > 1 && (
            <>
              <label className="field-label">Conto</label>
              <select className="inp inp-sm" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                {(accounts ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </>
          )}
        </div>
        {/* Cosa succede davvero: nessun soldo si muove. Sulle fatture, anche
            cosa resta su FattureInCloud e dove finisce la chiusura. */}
        <div className="px-4 pb-3 text-[12px] mute" style={{ lineHeight: 1.45 }}>
          {incasso ? 'Non si muove nessun soldo: si segna soltanto che il cliente ha pagato.' : 'Non parte nessun bonifico: si segna soltanto che è stata pagata.'}
          {withNote && (
            <>
              {' '}Su FattureInCloud resta {incasso ? 'da incassare' : 'da pagare'} finché il commercialista non la registra: la
              trovi in Banca, fra i pagamenti da comunicare al commercialista.
            </>
          )}
          {avviso && <span className="block mt-1.5" style={{ color: 'var(--text)', fontWeight: 600 }}>{avviso}</span>}
        </div>
        <div className="adm-pop-ft">
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-sm btn-primary" disabled={pending || !paidDate} onClick={submit}>
            <Icon name="check" size={10} /> {titolo}
          </button>
        </div>
      </div>
    </>
  );
  return mobile ? createPortal(corpo, document.body) : corpo;
}
